import { supabase, supabaseUrl } from './supabase'
import type {
  AuditItem,
  BootstrapData,
  CatalogDraft,
  CmsUser,
  CollectionOverview,
  DashboardData,
  DuplicateGroup,
  EventDraft,
  EventListResponse,
  EventSnapshot,
  EventSummary,
  ImportedDraft,
  IngressoState,
  IngressoTheater,
  MergeResult,
  OpenedEvent,
  PlaceAmenities,
  PlaceEntry,
  PlaceFaq,
  PlaceForm,
  PlaceListResponse,
  PlaceRecord,
  PopularLineup,
  PopularOverview,
  PopularSection,
  ProducerEntry,
  ProducerListResponse,
  ProducerRecord,
  ProfileForm,
  SourcePlace,
  SourcesOverview,
  TaxonomyData,
  CategoryConfig,
  ThemeConfig,
} from '../types'

const apiUrl = `${supabaseUrl}/functions/v1/cms-events`

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const { data } = await supabase.auth.getSession()
  const token = data.session?.access_token
  if (!token) throw new Error('Faça login para continuar.')

  const response = await fetch(`${apiUrl}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(init?.headers ?? {}),
    },
  })
  const payload = await response.json().catch(() => ({}))
  if (!response.ok) {
    throw new Error(payload.error || 'Não foi possível concluir a operação.')
  }
  return payload as T
}

async function uploadImage(dataUrl: string, kind: 'event' | 'place' | 'producer' = 'event') {
  const { data } = await supabase.auth.getSession()
  const token = data.session?.access_token
  if (!token) throw new Error('Faça login para continuar.')

  const response = await fetch('/api/images', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ data_url: dataUrl, kind }),
  })
  const payload = await response.json().catch(() => ({}))
  if (!response.ok) throw new Error(payload.error || 'Não foi possível enviar a imagem.')
  return payload as { image_url: string; object_path: string }
}

// The image a link points to, fetched by the CMS's own server (most sites
// don't let a browser read their images), as a file to prepare and upload.
async function imageFromLink(url: string) {
  const { data } = await supabase.auth.getSession()
  const token = data.session?.access_token
  if (!token) throw new Error('Faça login para continuar.')

  const response = await fetch('/api/images/source', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ url }),
  })
  if (!response.ok) {
    const payload = await response.json().catch(() => ({}))
    throw new Error(payload.error || `O CMS não conseguiu baixar a imagem desse link (${response.status}).`)
  }
  const blob = await response.blob()
  return new File([blob], 'imagem-do-link', { type: blob.type })
}

export const cmsApi = {
  taxonomy: () => request<TaxonomyData>('/taxonomy'),
  saveTaxonomy: (kind: 'category' | 'theme', item: CategoryConfig | ThemeConfig) =>
    request<{ ok: boolean }>('/taxonomy', { method: 'POST', body: JSON.stringify({ kind, item, request_id: crypto.randomUUID() }) }),
  reorderTaxonomy: (kind: 'category' | 'theme', slugs: string[]) =>
    request<{ ok: boolean }>('/taxonomy/order', { method: 'POST', body: JSON.stringify({ kind, slugs, request_id: crypto.randomUUID() }) }),
  bootstrap: () => request<BootstrapData>('/bootstrap'),

  dashboard: () => request<DashboardData>('/dashboard'),

  listEvents: (params: URLSearchParams) =>
    request<EventListResponse>(`/events?${params.toString()}`),

  // Events like a title being typed, the likeliest first.
  similarEvents: async (title: string, excludeId?: string) => (await request<{ items: EventSummary[] }>(
    `/events/similar?${new URLSearchParams({ title, ...(excludeId ? { exclude: excludeId } : {}) }).toString()}`,
  )).items,

  duplicateGroups: async () => (await request<{ groups: DuplicateGroup[] }>('/events/duplicates')).groups,

  // Joins another event into this one, which stays.
  mergeEvents: (keepId: string, mergedId: string) =>
    request<MergeResult>(`/events/${encodeURIComponent(keepId)}/merge`, {
      method: 'POST',
      body: JSON.stringify({ request_id: crypto.randomUUID(), merged_id: mergedId }),
    }),

  getEvent: (eventId: string) =>
    request<OpenedEvent>(`/events/${encodeURIComponent(eventId)}`),

  // From a draft, publishing also closes it. The questions of its places
  // changed in the editor are saved with it (PRD.md, item 99).
  publishEvent: (payload: EventSnapshot, draftId?: string | null, placeFaqs: PlaceFaq[] = []) =>
    request<{ ok: boolean; event_id: string; snapshot: EventSnapshot }>('/events', {
      method: 'POST',
      body: JSON.stringify({ request_id: crypto.randomUUID(), payload, draft_id: draftId || undefined, place_faqs: placeFaqs }),
    }),

  placeFaqs: async (placeIds: string[]) => placeIds.length
    ? (await request<{ items: PlaceFaq[] }>(`/place-faqs?${new URLSearchParams({ places: placeIds.join(',') }).toString()}`)).items
    : [],

  // Places and profiles begun in Lugares and Perfis (PRD.md, item 98).
  listCatalogDrafts: async (kind: CatalogDraft['kind']) =>
    (await request<{ items: CatalogDraft[] }>(`/catalog-drafts?kind=${kind}`)).items,

  getCatalogDraft: <T>(draftId: string) =>
    request<{ id: string; kind: CatalogDraft['kind']; content: T; updated_at: string }>(`/catalog-drafts/${encodeURIComponent(draftId)}`),

  saveCatalogDraft: (draftId: string, kind: CatalogDraft['kind'], content: PlaceForm | ProfileForm) =>
    request<{ id: string; updated_at: string }>('/catalog-drafts', {
      method: 'POST',
      body: JSON.stringify({ id: draftId, kind, content }),
    }),

  discardCatalogDraft: (draftId: string) =>
    request<{ ok: boolean }>(`/catalog-drafts/${encodeURIComponent(draftId)}/discard`, { method: 'POST', body: '{}' }),

  listDrafts: () => request<{ items: EventDraft[] }>('/drafts'),

  getDraft: (draftId: string) =>
    request<{ id: string; snapshot: EventSnapshot; updated_at: string; origin?: 'cms' | 'import'; source_url?: string | null; warnings?: string[] }>(
      `/drafts/${encodeURIComponent(draftId)}`),

  // Events imported from a link (PRD.md, item 93).
  listReview: () => request<{ items: ImportedDraft[] }>('/review'),

  // The first save creates the draft under this id; the next ones update it.
  saveDraft: (draftId: string, snapshot: EventSnapshot) =>
    request<{ id: string; updated_at: string }>('/drafts', {
      method: 'POST',
      body: JSON.stringify({ id: draftId, snapshot }),
    }),

  discardDraft: (draftId: string) =>
    request<{ ok: boolean }>(`/drafts/${encodeURIComponent(draftId)}/discard`, { method: 'POST', body: '{}' }),

  setEventStatus: (eventId: string, status: string, changeNote: string) =>
    request<{ ok: boolean; event_id: string; status: string }>(
      `/events/${encodeURIComponent(eventId)}/status`,
      {
        method: 'POST',
        body: JSON.stringify({
          request_id: crypto.randomUUID(),
          status,
          change_note: changeNote || null,
        }),
      },
    ),

  // An item fixed by hand returns to the automatic collection.
  followSource: (eventId: string, kind: 'event' | 'run' | 'occurrence', id: string) =>
    request<{ ok: boolean; snapshot: EventSnapshot }>(`/events/${encodeURIComponent(eventId)}/follow-source`, {
      method: 'POST',
      body: JSON.stringify({ request_id: crypto.randomUUID(), kind, id }),
    }),

  uploadImage,

  imageFromLink,

  // Best effort: the media job hashes any cover this misses.
  saveMediaBlurhash: (item: { url: string; blurhash: string; width: number; height: number }) =>
    request<{ ok: boolean }>('/media-blurhashes', { method: 'POST', body: JSON.stringify(item) }),

  listPlaces: (params: URLSearchParams) =>
    request<PlaceListResponse>(`/places?${params.toString()}`),

  getPlace: (placeId: string) =>
    request<PlaceEntry>(`/places/${encodeURIComponent(placeId)}`),

  // With a profile, the place and its venue profile at once, or neither; then
  // the questions changed, and the draft it came from closes.
  savePlace: (place: Partial<PlaceRecord>, options: { profile?: Partial<ProducerRecord>; faqs?: PlaceFaq[]; draftId?: string | null } = {}) =>
    request<{ ok: boolean; place: PlaceRecord; producer?: ProducerRecord }>('/places', {
      method: 'POST',
      body: JSON.stringify({
        request_id: crypto.randomUUID(), place,
        ...(options.profile ? { profile: options.profile } : {}),
        ...(options.faqs?.length ? { faqs: options.faqs } : {}),
        ...(options.draftId ? { draft_id: options.draftId } : {}),
      }),
    }),

  // The places the ingestion reads, and finding new ones at Ingresso.com.
  listSources: () => request<SourcesOverview>('/sources'),

  collectionRuns: () => request<CollectionOverview>('/sources/runs'),

  runCollection: (source: string) =>
    request<{ ok: boolean; source: string }>('/sources/runs', { method: 'POST', body: JSON.stringify({ source }) }),

  saveSourcePlace: (item: Partial<SourcePlace>) =>
    request<{ ok: boolean; source_place: SourcePlace }>('/sources/places', {
      method: 'POST',
      body: JSON.stringify({ request_id: crypto.randomUUID(), item }),
    }),

  ingressoCities: async () => (await request<{ states: IngressoState[] }>('/sources/ingresso/cities')).states,

  ingressoTheaters: async (cityId: string) =>
    (await request<{ items: IngressoTheater[] }>(`/sources/ingresso/theaters?${new URLSearchParams({ city: cityId }).toString()}`)).items,

  deletePlace: (placeId: string) =>
    request<{ ok: boolean; place_id: string }>(`/places/${encodeURIComponent(placeId)}/delete`, {
      method: 'POST',
      body: JSON.stringify({ request_id: crypto.randomUUID() }),
    }),

  listProducers: (params: URLSearchParams) =>
    request<ProducerListResponse>(`/producers?${params.toString()}`),

  getProducer: (producerId: string) =>
    request<ProducerEntry>(`/producers/${encodeURIComponent(producerId)}`),

  saveProducer: (producer: Partial<ProducerRecord>, draftId?: string | null) =>
    request<{ ok: boolean; producer: ProducerRecord }>('/producers', {
      method: 'POST',
      body: JSON.stringify({ request_id: crypto.randomUUID(), producer, ...(draftId ? { draft_id: draftId } : {}) }),
    }),

  deleteProducer: (producerId: string) =>
    request<{ ok: boolean; producer_id: string }>(`/producers/${encodeURIComponent(producerId)}/delete`, {
      method: 'POST',
      body: JSON.stringify({ request_id: crypto.randomUUID() }),
    }),

  updatePlaceAmenities: (placeId: string, amenities: Partial<PlaceAmenities>) =>
    request<{ ok: boolean; place_id: string; amenities: PlaceAmenities }>(
      `/places/${encodeURIComponent(placeId)}/amenities`,
      {
        method: 'POST',
        body: JSON.stringify({ request_id: crypto.randomUUID(), amenities }),
      },
    ),

  // Popular for a city; with none, the first that qualifies.
  getPopular: (city: string) =>
    request<PopularOverview>(`/popular?${new URLSearchParams({ city }).toString()}`),

  // Every city's Popular now, the calendar of the next swaps.
  popularLineup: async () => (await request<{ items: PopularLineup[] }>('/popular/lineup')).items,

  savePopularPicks: (city: string, section: PopularSection, ids: string[]) =>
    request<PopularOverview>('/popular', {
      method: 'POST',
      body: JSON.stringify({ request_id: crypto.randomUUID(), city, section, ids }),
    }),

  listUsers: async () => (await request<{ items: CmsUser[] }>('/users')).items,

  saveUser: (email: string, role: string, isActive: boolean) =>
    request<CmsUser>('/users', {
      method: 'POST',
      body: JSON.stringify({
        request_id: crypto.randomUUID(),
        email,
        role,
        is_active: isActive,
      }),
    }),

  // A page of the history; next continues it.
  listAudit: (params: URLSearchParams) => request<{ items: AuditItem[]; next: string | null }>(`/audit?${params}`),
}
